import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { mq } from '../../../../../../common/src/utils/styledComponentsUtils';
import { Grid, useBreakpointValue } from '@wegroup/design-system';

// Components
import ConditonalWrapper from '../../../../../../common/src/components/ConditionalWrapper';
import SingleAnnex from './SingleAnnex';
import DashedButton from '../../../ui/advisory-flow/components/shared-components/DashedButton';
import DashedButtonModal from '../accident/DashedButtonModal';
import { Alert } from 'wg-fe-ui';
import AnnexForm from './AnnexForm';

// Redux

// Types
import { Annex } from '../../../../../../../types/SMT';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import { useFlowsDispatch } from '../../../../../../common/src/utils/globalVariables';
import { setToastErrorMessage } from '../../../../store/redux/slices/flowsSlice';

interface Props {
  question: FlowsQuestion;
}

const AnnexesInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const flowsDispatch = useFlowsDispatch();
  const {
    field: { value, onBlur },
  } = useFlowInputComponent({
    question,
    defaultValue: [],
  });

  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<number[]>([]);

  const annexes = (value as Annex[]) || [];

  useEffect(() => {
    if (isEditing.length === 0 && !isAdding)
      flowsDispatch(setToastErrorMessage(t('')));
    else {
      flowsDispatch(
        setToastErrorMessage(t('Please finish editing or adding the annexes')),
      );
    }
  }, [isEditing, isAdding]);

  useEffect(() => {
    return () => {
      setIsEditing([]);
    };
  }, []);

  const handleSave = (annex: Annex) => {
    setIsAdding(false);
    onBlur({
      name: question.id,
      value: [...annexes, annex],
    });
  };

  const handleEdit = (annex: Annex, index: number) => {
    if (index > -1) {
      const newAnnexes = [...annexes];
      newAnnexes[index] = annex;
      onBlur({
        name: question.id,
        value: newAnnexes,
      });
    }
  };

  const handleRemove = (index: number) => {
    onBlur({
      name: question.id,
      value: annexes.filter((_, _index) => _index !== index),
    });
    handleIsEditing(false, index);
  };

  const handleIsEditing = (isEditing: boolean, index: number) => {
    if (isEditing) setIsEditing((prev) => [...prev, index]);
    else setIsEditing((prev) => prev.filter((_index) => _index !== index));
  };

  const isMobile = !!useBreakpointValue({ base: true, laptop: false });

  return (
    <>
      {isMobile && (
        <StyledAlert icon type="warning">
          {t('Tap one of your annexes in order to edit or remove them')}
        </StyledAlert>
      )}
      <Grid
        width="100%"
        templateColumns={{ base: '1fr', mobileL: 'repeat(auto-fill, 22rem)' }}
        gridGap="6"
        justifyContent="center"
        data-test-id="ProdFac_AnnexesInputComponent"
      >
        {annexes.map((annex, index) => (
          <SingleAnnex
            key={`annex_${index}`}
            annex={annex}
            isMobile={isMobile}
            handleEdit={(annex: Annex) => handleEdit(annex, index)}
            handleRemove={() => handleRemove(index)}
            isEditing={isEditing.includes(index)}
            setIsEditing={(isEditing: boolean) =>
              handleIsEditing(isEditing, index)
            }
          />
        ))}

        {isAdding ? (
          <ConditonalWrapper
            condition={isMobile}
            wrapper={(children) => (
              <DashedButtonModal isOpen={isAdding}>
                {children}
              </DashedButtonModal>
            )}
          >
            <AnnexForm
              handleSave={handleSave}
              handleCancel={() => setIsAdding(false)}
            />
          </ConditonalWrapper>
        ) : (
          <StyledDashedButton
            label={t('Add a new annex')}
            onClick={() => setIsAdding(true)}
          />
        )}
      </Grid>
    </>
  );
};

const StyledAlert = styled(Alert)`
  padding: 1.6rem;
  margin-bottom: 2.4rem;
  width: unset;

  > div {
    font-size: 1.4rem;
  }
`;

const StyledDashedButton = styled(DashedButton)`
  flex-direction: row;
  align-items: center;
  padding: 1.6rem;
  height: fit-content;

  > div {
    margin-left: 1.6rem;
    margin-top: 0;
  }

  > button {
    padding: 0.2rem;
    height: 3.5rem;
    width: 3.5rem;
  }

  ${mq.mobileL} {
    flex-direction: column;
    padding: 4rem;
    width: 22rem;
    height: 22rem;

    > div {
      margin-left: 0;
      margin-top: 1.6rem;
    }

    > button {
      height: 5rem;
      width: 5rem;
      padding: 0.8rem;
    }
  }
`;

export default AnnexesInputComponent;
